import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import {
    AssetRoutine,
    AssetRoutineState,
    FIND_ROUTINE_DRAFT,
    Issue,
    RoutineConfig,
    RoutineWorkflow,
    Severity,
    useCurrentWorkspace,
    useCustomQuery,
    useFindByQuery,
    WorkspaceAsset,
    WorkspaceRoutine,
    WorkspaceType,
} from '@gripp/shared-logic';
import { extractAndDeduplicateRoutines } from 'libs/shared-ui/src/utils/assetHelpers';
import { uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
    Platform,
    ScrollView,
    StyleProp,
    StyleSheet,
    TextStyle,
    View,
    ViewStyle,
} from 'react-native';
import { Colors } from '../../../themes/colors';
import LoadingIndicator from '../../loadingIndicator';
import { RoutineModule } from '../types';
import { EditRequestRoutine } from './editRoutine';
import { RoutineModuleButton } from './routineModuleButton';

type RoutineButtonsProps = {
  workspaceAsset: WorkspaceAsset;
  showAll: boolean;
  initialRoutinesToShow: number;
  hideAssignRoutinesButton?: boolean;
  onRoutineSelected: (props: RoutineExecutionProps) => void;
  buttonStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  buttonIcon?: Severity;
  buttonIconStyle?: StyleProp<ViewStyle>;
  buttonsContainerStyle?: StyleProp<ViewStyle>;
};

export type RoutineExecutionProps = {
  workspaceAsset: WorkspaceAsset;
  module: RoutineModule<any, any>;
  routine?: WorkspaceRoutine;
  state?: AssetRoutineState<any>;
  additionalState?: any;
  sourceIssue?: Issue;
  workflows?: RoutineWorkflow[];
  isIssueEditRoutine?: boolean;
  assetRoutine?: AssetRoutine;
  onCanceled?: () => void;
  onCompleted?: () => void;
};

export const RoutineButtons = (props: RoutineButtonsProps) => {
  const { workspaceAsset, showAll, initialRoutinesToShow } = props;
  const currentWorkspace = useCurrentWorkspace();
  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;

  const orFilters: any[] = [
    { workspaceAsset: { eq: workspaceAsset.id } },
    { group: { in: workspaceAsset.groups?.map((g) => g.id) || [] } },
  ];

  if (isOEM && workspaceAsset.asset?.product?.id) {
    orFilters.push({ product: { eq: workspaceAsset.asset.product.id } });
  }

  const { data: workflowData, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: {
      filter: {
        or: orFilters,
        and: {
          disabled: {
            ne: true,
          },
        },
      },
    },
  });

  const isOneTime = (routine: WorkspaceRoutine) =>
    !!(routine.config as any)?.oneTimeOnly;

  const hasState = useCallback(
    (routine: WorkspaceRoutine, asset: WorkspaceAsset) =>
      workspaceAsset.routineState?.some(
        (s) => s.workspaceRoutineId === routine.id
      ),
    [workspaceAsset.routineState]
  );

  const buttonData = useMemo(() => {
    const allExternalRoutines: WorkspaceRoutine[] =
      extractAndDeduplicateRoutines(workspaceAsset);

    const workspaceRoutines = workspaceAsset.routines || [];

    // If there are no routines assigned to the asset, but the product has routines, assign them
    if (
      !isLoading &&
      workspaceRoutines.length === 0 &&
      allExternalRoutines.length > 0
    ) {
      // Remove workspace routines that have the same ID as product routines
      const productRoutineIds = new Set(
        allExternalRoutines.map((pr: any) => pr.id)
      );

      workspaceRoutines.filter(
        (wr: WorkspaceRoutine) => !productRoutineIds.has(wr.id)
      );
    }

    const productRoutineAdjusted = allExternalRoutines.map(
      (pr: WorkspaceRoutine) => ({
        routine: pr,
      })
    );

    if (!workspaceRoutines.length && !allExternalRoutines.length) {
      return [];
    }

    const allActive = workspaceRoutines.filter((r) => !r.deactivated);

    // Determine if any one-time-only routines are pending (no state yet)
    const pendingOneTime = allActive.filter((r) => {
      if (!isOneTime(r)) return false;
      return !hasState(r, workspaceAsset); // pending if no state exists
    });

    // If there are pending one-time routines, show only those. Otherwise, hide any one-time routines that have already been completed
    const visible =
      pendingOneTime.length > 0
        ? pendingOneTime
        : allActive.filter((r) => {
            if (!isOneTime(r)) return true;
            return !hasState(r, workspaceAsset); // hide one-time after submission
          });

    const sortedVisible = visible
      .sort((a, b) => {
        let routineA, routineB;

        if (
          workspaceAsset.routineOrder &&
          workspaceAsset.routineOrder.length > 0
        ) {
          routineA = workspaceAsset.routineOrder.find(
            (x) => x.workspaceRoutineId === a.id
          );
          routineB = workspaceAsset.routineOrder.find(
            (x) => x.workspaceRoutineId === b.id
          );
        }
        const aOrder = routineA?.order ?? a.order;
        const bOrder = routineB?.order ?? b.order;
        return aOrder - bOrder;
      })
      .map((routine) => ({
        routine,
        state: workspaceAsset.routineState?.find(
          (state) => state.workspaceRoutineId === routine.id
        ),
      }));

    const combineRoutines = uniqBy(
      [...productRoutineAdjusted, ...sortedVisible],
      'routine.id'
    );

    if (combineRoutines.length > initialRoutinesToShow && !showAll) {
      return combineRoutines.slice(0, initialRoutinesToShow);
    }

    return combineRoutines;
  }, [workspaceAsset, isLoading, showAll, initialRoutinesToShow, hasState]);

  const getRoutineWorkflows = useCallback(
    (id: string, config: RoutineConfig, state: any) => {
      if (!id || !workflowData?.items?.length) return [];

      return workflowData.items
        .filter(
          (item: RoutineWorkflow) =>
            item.trigger?.config?.workspaceRoutineId === id
        )
        .map((workflow: RoutineWorkflow) => {
          return { ...workflow, uom: config?.uom, count: state?.count };
        });
    },
    [workflowData]
  );

  const onClick = (
    routine: WorkspaceRoutine,
    module: RoutineModule<any, any>,
    state?: AssetRoutineState<any>,
    additionalState?: any,
    workflows?: RoutineWorkflow[]
  ) => {
    props.onRoutineSelected({
      workspaceAsset,
      routine,
      module,
      state,
      additionalState,
      workflows,
    });
  };

  const Buttons = () => {
    if (buttonData?.length === 0) return null;

    return buttonData.map((data: any, index: number) => {
      const workspaceRoutineId = data.routine?.id;

      const config = data.routine?.config;
      const state = data.state?.state;

      const workflows = getRoutineWorkflows(
        workspaceRoutineId ?? '',
        config,
        state
      );

      const hasWorkflows = workflows.length > 0;

      const alertIconStyle = props.buttonStyle
        ? [styles.alertIconBase, styles.alertIcon]
        : styles.alertIconBase;
      
      // Check for draft
      const DraftIndicator = ({ routineId }: { routineId: string }) => {
        const { data: draftData } = useCustomQuery({
          cacheKey: ['routineDraft', workspaceAsset?.id, routineId],
          query: FIND_ROUTINE_DRAFT,
          variables: {
            workspaceAssetId: workspaceAsset?.id || '',
            routineId: routineId,
          },
          options: {
            enabled: !!(workspaceAsset?.id && routineId),
          },
        });
        
        const hasDraft = draftData?.routine?.findRoutineDraft;
        
        if (!hasDraft) return null;
        
        return (
          <MaterialCommunityIcons
            name="pencil"
            style={styles.draftIcon}
          />
        );
      };
      
      return (
        <RoutineModuleButton
          key={`routine-button-${index}`}
          routine={data.routine}
          state={data.state as AssetRoutineState<any>}
          buttonIndex={index}
          buttonCount={buttonData.length}
          buttonStyle={props.buttonStyle}
          buttonIcon={props.buttonIcon}
          buttonIconStyle={props.buttonIconStyle}
          labelStyle={[props.labelStyle, { flexShrink: 1 }]}
          onClick={onClick}
          leftButtonIcon={<DraftIndicator routineId={workspaceRoutineId || ''} />}
          secondaryButtonIcon={
            hasWorkflows && (
              <MaterialCommunityIcons
                name="alarm-check"
                style={alertIconStyle}
              />
            )
          }
          workflows={workflows}
        />
      );
    });
  };

  return (
    <ScrollView
      scrollEnabled={showAll}
      showsVerticalScrollIndicator={false}
      style={
        props.buttonsContainerStyle
          ? props.buttonsContainerStyle
          : styles.scrollView
      }
    >
      {isLoading ? <LoadingIndicator flexContainer /> : <Buttons />}
      {(buttonData.length < initialRoutinesToShow || showAll) &&
        !props.hideAssignRoutinesButton && (
          <View style={buttonData.length > 1 && { marginTop: 16 }}>
            <EditRequestRoutine workspaceAsset={workspaceAsset} />
          </View>
        )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 0,
  },
  alertIconBase: {
    fontSize: 22,
    color: Colors.white,
  },
  alertIcon: {
    ...Platform.select({
      web: {
        color: Colors.primaryDark,
      },
    }),
  },
  draftIcon: {
    fontSize: 20,
    color: Colors.white,
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
      }
    }
  }
`;
